/**
 * @file CustomerBlendPicker.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-02
 * @description Pick the customer and the blend line a formulation belongs to.
 *
 *              Two controls, because the destination is two things: which farm,
 *              and which of that farm's lines. Splitting them is what lets a
 *              customer with no lines at all be a valid destination — the old
 *              single "move into an existing line" dropdown made such a
 *              customer unreachable, which is exactly the case someone opens
 *              the organiser to fix.
 *
 *              The line control offers what the customer already has and
 *              accepts anything typed, so creating "Youngstock" and filing into
 *              the existing "Milkers" are the same gesture.
 *
 *              Shared by MoveFormulationDialog (one row) and
 *              CustomerBlendOrganiser (many), so the two cannot drift.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import SearchableDropdown from './SearchableDropdown';
import ComboBox from './ComboBox';

/**
 * Loads the account list once and hands it to every picker on screen.
 *
 * The organiser can show fifty rows; fifty copies of a 4,000-row fetch is not a
 * dropdown, it is an outage.
 */
export function useBlendAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/formulations/customer-blends/accounts`,
                                { credentials: 'include' });
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) throw new Error(data.error || 'Failed to load customers');
        setAccounts(data.accounts || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(() => accounts.map(a => ({
    value: String(a.id),
    // The farm rather than the postcode as the second line: two "J Smith & Son"
    // rows are told apart by where the lorry goes.
    label: a.delivery_line1 ? `${a.company_name} · ${a.delivery_line1}` : a.company_name,
    searchText: [a.account_no, a.company_name, a.delivery_line1, a.delivery_postcode]
      .filter(Boolean).join(' ')
  })), [accounts]);

  const byId = useMemo(
    () => new Map(accounts.map(a => [String(a.id), a])),
    [accounts]
  );

  return { accounts, options, byId, loading, error };
}

function CustomerBlendPicker({
  accountOptions,
  accountsById,
  blends = [],
  value = {},              // { accountId, name }
  onChange,
  onViewCustomer,
  disabled = false,
  compact = false,
  // Set when the organiser has been asked to save and this row has a customer
  // but no line. Marks the line control rather than the whole picker: the
  // customer half is answered, and coluring both would send the eye to the
  // control that is already correct.
  invalid = false
}) {
  const account = value.accountId ? accountsById.get(String(value.accountId)) : null;

  const lineOptions = useMemo(() => {
    if (!value.accountId) return [];
    return blends
      .filter(b => String(b.account_id) === String(value.accountId))
      .map(b => ({ value: b.name, label: b.name }));
  }, [blends, value.accountId]);

  const setAccount = useCallback((v) => {
    // Default to the customer's existing line when they have exactly one —
    // most farms do, and picking "Milkers" from a list of one is busywork.
    const lines = blends.filter(b => String(b.account_id) === String(v));
    onChange({
      accountId: v || null,
      name: lines.length === 1 ? lines[0].name : (value.name || '')
    });
  }, [blends, onChange, value.name]);

  return (
    <div className={`cbp${compact ? ' cbp-compact' : ''}`}>
      <div className="cbp-account">
        <SearchableDropdown
          value={value.accountId ? String(value.accountId) : ''}
          onChange={setAccount}
          options={accountOptions}
          placeholder="Which customer..."
          disabled={disabled}
        />
        {/* Before filing it under them, check what else they buy. */}
        <button
          className={`btn-icon${!account ? ' btn-disabled' : ''}`}
          disabled={!account}
          title={account
            ? `See what ${account.company_name} already buys`
            : 'Pick a customer first'}
          onClick={() => account && onViewCustomer(account)}
        >
          <span className="material-symbols-rounded">visibility</span>
        </button>
      </div>

      <div className={`cbp-line${invalid ? ' cbp-line-invalid' : ''}`}>
        <ComboBox
          value={value.name || ''}
          onChange={(v) => onChange({ ...value, name: v })}
          options={lineOptions}
          placeholder={value.accountId ? 'Blend line, e.g. Milkers' : 'Pick a customer first'}
          disabled={disabled || !value.accountId}
        />
      </div>
    </div>
  );
}

export default CustomerBlendPicker;
